/**
 * The requirement context bar + flow stepper shown above RFQ → Suppliers → Comparison →
 * Quotation (prototype `ctxBar`).
 *
 * The "N days" chips are derived here from the enquiry's timestamps — never from a stored
 * number (contract §1, §3). The board-wide "awaiting > 2 days" count is a separate,
 * backend-aggregated figure and does not belong on this bar.
 */

import { Link, useLocation } from 'react-router-dom'
import { Badge } from './Badge'
import { awaitingDays, effectiveStatus, enquiryTypeTone, formatAge } from '../lib/format'
import type { Enquiry } from '../lib/types'

const FLOW = [
  { key: 'rfq', label: 'RFQ' },
  { key: 'suppliers', label: 'Suppliers' },
  { key: 'compare', label: 'Comparison' },
  { key: 'quote', label: 'Quotation' },
] as const

export function ContextBar({ enquiry, active }: { enquiry: Enquiry; active: (typeof FLOW)[number]['key'] }) {
  const location = useLocation()
  const activeIndex = FLOW.findIndex((f) => f.key === active)
  const status = effectiveStatus(enquiry.status, enquiry.age_days)
  const awaiting = awaitingDays(enquiry.age_days)

  return (
    <div className="ctx">
      {/* The number users recognise; `enquiry.id` is still what every link below routes with. */}
      <span className="rid" title={enquiry.ref === enquiry.id ? undefined : enquiry.id}>
        {enquiry.ref}
      </span>
      <span className="cust">{enquiry.header.customer}</span>
      {/* Same tone mapping as the board card — a Commercial enquiry shouldn't be teal in
          one place and blue in another. */}
      <Badge tone={enquiryTypeTone(enquiry.header.enquiry_type)}>{enquiry.header.enquiry_type}</Badge>
      <span className="meta">
        {status} · {enquiry.header.sales_person}→{enquiry.header.purchaser} · {formatAge(enquiry.created_at)} old
      </span>
      {awaiting >= 1 ? <Badge tone={awaiting > 2 ? 'warn' : 'muted'}>Awaiting {awaiting}d</Badge> : null}

      <div className="stepper">
        {FLOW.map((step, i) => (
          <Link
            key={step.key}
            to={`/${step.key}/${enquiry.id}`}
            state={location.state}
            className={`step ${step.key === active ? 'active' : i < activeIndex ? 'done' : ''}`}
          >
            <span className="n">{i < activeIndex ? '✓' : i + 1}</span>
            {step.label}
          </Link>
        ))}
      </div>
    </div>
  )
}
